import { Container, styled, Typography } from "@mui/material";
import RecentHistoryBreadcrumbs from "./RecentHistoryBreadcrumbs";
import { useFlags } from "launchdarkly-react-client-sdk";
import type { FC, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import useSettings from "../../hooks/useSettings";
import FullPageLoader from "../FullPageLoader";
import ShortcutsProvider from "../shortcuts/ShortcutsContext";
import ShortcutsModal from "../shortcuts/ShortcutsModal";
import DashboardNavbar from "./DashboardNavbar";
import DashboardSidebar from "./DashboardSidebar";
import NotificationComponent from "./NotificationComponent";
import SubscriptionStatusBanner from "./SubscriptionStatusBanner";
import PaymentMethodPromptGate from "../../pages/subscription/PaymentMethodPromptGate";
import TermsAcceptanceGate from "../../pages/subscription/TermsAcceptanceGate";
import { useScroll } from "../../contexts/ScrollContext";
import useEntitlements from "../../hooks/useEntitlements";
import useRoles from "../../hooks/useRoles";
import { hasSubscriptionManagerRole } from "../../utils/roleUtils";

interface DashboardLayoutProps {
  children?: ReactNode;
}

const DashboardLayoutRoot = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  display: "flex",
  height: "100%",
  overflow: "hidden",
  width: "100%",
}));

const DashboardLayoutWrapper = styled("div", {
  shouldForwardProp: (prop) => prop !== "collapsed",
})<{ collapsed: boolean }>(({ theme, collapsed }) => ({
  display: "flex",
  flex: "1 1 auto",
  overflow: "hidden",
  transition: "padding-left 0.3s ease",
  paddingTop: "56px",
  [theme.breakpoints.up("lg")]: {
    paddingLeft: collapsed ? "72px" : "280px",
  },
}));

const DashboardLayoutContainer = styled("div")({
  display: "flex",
  flex: "1 1 auto",
  overflow: "hidden",
});

const DashboardLayoutContent = styled("div")({
  flex: "1 1 auto",
  height: "100%",
  overflow: "auto",
  position: "relative",
  WebkitOverflowScrolling: "touch",
});

const DashboardLayout2: FC<DashboardLayoutProps> = () => {
  const { settings } = useSettings();
  const { allowShortcutsMenu } = useFlags();
  const { monitorTranscriptAutoScroll, setTextContainerRef } = useScroll();
  const textContainerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    entitlements,
    isLoading: entitlementsLoading,
    isOnboarded,
  } = useEntitlements();
  const userRoles = useRoles();

  const [isSidebarMobileOpen, setIsSidebarMobileOpen] =
    useState<boolean>(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const stored = localStorage.getItem("sidebarCollapsed");
    return stored ? JSON.parse(stored) : false;
  });

  const handleToggleSidebar = () => {
    const newCollapsed = !isSidebarCollapsed;
    setIsSidebarCollapsed(newCollapsed);
    localStorage.setItem("sidebarCollapsed", JSON.stringify(newCollapsed));
  };

  const [loading] = useFullPageLoader();

  useEffect(() => {
    setTextContainerRef(textContainerRef);
  }, [setTextContainerRef, textContainerRef]);

  // Handle redirection for restricted organizations
  useEffect(() => {
    const isSubscriptionManager = hasSubscriptionManagerRole(userRoles);
    if (
      !entitlementsLoading &&
      entitlements?.isRestricted &&
      isOnboarded("subscription") &&
      isSubscriptionManager
    ) {
      const allowedPaths = [
        "/subscription",
        "/subscription/billing-history",
        "/auth/logout",
        "/organization",
      ];
      const isAllowedPath = allowedPaths.some(
        (path) =>
          location.pathname === path || location.pathname.startsWith(path),
      );

      if (!isAllowedPath) {
        navigate("/subscription", { replace: true });
      }
    }
  }, [
    entitlements,
    entitlementsLoading,
    location.pathname,
    navigate,
    isOnboarded,
    userRoles,
  ]);

  return (
    <ShortcutsProvider>
      <DashboardLayoutRoot>
        <DashboardNavbar
          onSidebarMobileOpen={(): void => setIsSidebarMobileOpen(true)}
        />
        <DashboardSidebar
          onMobileClose={(): void => setIsSidebarMobileOpen(false)}
          openMobile={isSidebarMobileOpen}
          collapsed={isSidebarCollapsed}
          handleToggleCollapsed={handleToggleSidebar}
        />
        <DashboardLayoutWrapper collapsed={isSidebarCollapsed}>
          <DashboardLayoutContainer>
            <DashboardLayoutContent
              id="dashboard-container"
              onScroll={monitorTranscriptAutoScroll}
              ref={textContainerRef}
            >
              <NotificationComponent />
              <SubscriptionStatusBanner />
              <Container
                sx={{ pt: 2, pb: 0 }}
                maxWidth={settings.compact ? "xl" : false}
              >
                <TermsAcceptanceGate />
                <PaymentMethodPromptGate />
                <RecentHistoryBreadcrumbs />
              </Container>
              <Container
                sx={{ py: 2 }}
                maxWidth={settings.compact ? "xl" : false}
              >
                <Outlet />
              </Container>
            </DashboardLayoutContent>
            <FullPageLoader loading={loading} />
          </DashboardLayoutContainer>
        </DashboardLayoutWrapper>
        {allowShortcutsMenu && <ShortcutsModal />}
      </DashboardLayoutRoot>
    </ShortcutsProvider>
  );
};

export default DashboardLayout2;
